// Phase 14 — Client dashboard for PROFESSIONAL hosts.
// Consolidated view of all events the pro hosts with status/tier badges and stats.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Copy, UserPlus, BookOpen, X, BarChart2 } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { listProEvents, cloneEvent } from '@/lib/pro-toolkit-api';
import { eventStatusLabel, statusPillClasses } from '@/lib/event-labels';
import TierBadge from '@/components/TierBadge';

const LOOKBOOK_STATE_LABELS = { DRAFT: 'Draft', LIVE: 'Live', PAUSED: 'Paused', UNPUBLISHED: 'Ended' };
function lookbookStateLabel(state) {
  if (!state) return '\u2014';
  return LOOKBOOK_STATE_LABELS[state] || state;
}

function StatCell({ label, value, testId }) {
  return (
    <div className="flex-1 min-w-0 bg-[#F3F5F8] px-1.5 py-1.5 rounded text-center" data-testid={testId}>
      <p className="text-[15px] font-extrabold text-[#0B1B46] truncate">{value}</p>
      <p className="text-[10px] text-[#5B6472] uppercase tracking-wide mt-0.5 truncate">{label}</p>
    </div>
  );
}

export default function ProClientsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Clone modal
  const [cloneTarget, setCloneTarget] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneStart, setCloneStart] = useState('');
  const [cloneEnd, setCloneEnd] = useState('');
  const [cloneBusy, setCloneBusy] = useState(false);
  const [cloneError, setCloneError] = useState(null);
  const isPro = (user?.account_type || '').toUpperCase() === 'PROFESSIONAL';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listProEvents();
      setEvents(res.events || []);
    } catch (e) {
      const msg = e?.message || 'Could not load client events.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openClone(ev) {
    setCloneTarget(ev);
    setCloneName(`${ev.name} (copy)`);
    const inMonth = new Date(Date.now() + 30 * 86400000);
    const isoDay = (d) => d.toISOString().slice(0, 16);
    setCloneStart(isoDay(inMonth).replace(/T\d{2}:\d{2}/, 'T18:00'));
    setCloneEnd(isoDay(inMonth).replace(/T\d{2}:\d{2}/, 'T23:00'));
    setCloneError(null);
  }

  async function onClone() {
    if (!cloneTarget) return;
    setCloneError(null);
    if (!cloneName.trim()) { setCloneError('Give the new event a name.'); return; }
    setCloneBusy(true);
    try {
      const s = cloneStart.trim();
      const e2 = cloneEnd.trim();
      if (!s) throw new Error('Start date is required');
      if (!e2) throw new Error('End date is required');
      const toIso = (v) => v.includes('T') && !/[+Z]/.test(v.slice(-6)) ? `${v}:00+00:00` : v;
      await cloneEvent(cloneTarget.id, { name: cloneName.trim(), start_at: toIso(s), end_at: toIso(e2) });
      setCloneTarget(null);
      setCloneName(''); setCloneStart(''); setCloneEnd('');
      await load();
    } catch (e) {
      setCloneError(e?.message || 'Could not clone event.');
    } finally {
      setCloneBusy(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="pro-clients-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <button
          onClick={() => navigate(-1)}
          className="p-1 rounded hover:bg-[#F3F5F8] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
          data-testid="pro-clients-back-btn"
          aria-label="Go back"
        >
          <ChevronLeft className="w-5 h-5 text-[#0B1B46]" />
        </button>
        <h1 className="text-xl font-extrabold text-[#0B1B46]" data-testid="pro-clients-title">Client dashboard</h1>
        <div className="w-5" />
      </div>
      <p className="text-sm text-[#5B6472] leading-5 mb-4" data-testid="pro-clients-welcome">
        Every event you host, sorted by date. Tap any card to open its dashboard.
      </p>
      <button onClick={() => navigate('/pro-analytics')}
        className="flex items-center gap-2 mb-4 text-sm font-bold text-[#146BFF] hover:underline"
        data-testid="pro-clients-analytics-link">
        <BarChart2 className="w-4 h-4" /> Cross-event analytics &rarr;
      </button>

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center py-10" data-testid="pro-clients-loading">
          <div className="w-7 h-7 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        /* Error */
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-5" data-testid="pro-clients-error">
          <p className="text-[13px] font-semibold text-[#DC2626]">{error}</p>
          {!isPro && (
            <p className="text-sm text-[#5B6472] mt-2">Switch to Professional in Settings to see your client dashboard.</p>
          )}
        </div>
      ) : events.length === 0 ? (
        /* Empty */
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-5" data-testid="pro-clients-empty">
          <p className="text-[11px] font-extrabold text-[#0B1B46] uppercase tracking-wide mb-1">No client events yet</p>
          <p className="text-sm text-[#5B6472]">Create your first event and it&apos;ll show up here alongside future clients.</p>
        </div>
      ) : (
        /* Event list */
        <div className="flex flex-col gap-3 pb-6">
          {events.map((ev) => (
            <button
              key={ev.id}
              onClick={() => navigate(`/host-dashboard?event_id=${encodeURIComponent(ev.id)}`)}
              className="bg-white border border-[#E3E8EF] rounded-xl p-4 text-left hover:border-[#146BFF] hover:shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
              data-testid={`pro-clients-card-${ev.id}`}
            >
              {/* Name + Tier */}
              <div className="flex items-center justify-between gap-2">
                <span className="text-base font-extrabold text-[#0B1B46] truncate flex-1" data-testid={`pro-clients-name-${ev.id}`}>
                  {ev.name}
                </span>
                <TierBadge
                  tier={ev.legacy_uncapped ? 'LEGACY' : (ev.premium_tier || 'FREE')}
                  size="sm"
                  testId={`pro-clients-tier-${ev.id}`}
                />
              </div>
              {/* Status + Date */}
              <div className="flex items-center gap-2 mt-1.5">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${statusPillClasses(ev.status)}`}
                  data-testid={`pro-clients-status-${ev.id}`}
                >
                  <span>{'•'}</span>{eventStatusLabel(ev.status)}
                </span>
                {ev.start_at && (
                  <span className="text-xs text-[#5B6472]" data-testid={`pro-clients-date-${ev.id}`}>
                    {new Date(ev.start_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
              {/* Stats */}
              <div className="flex gap-2 mt-3">
                <StatCell label="Uploads" value={String(ev.submission_stats?.approved_count ?? 0)} testId={`pro-clients-uploads-${ev.id}`} />
                <StatCell label="Contributors" value={String(ev.contributor_count ?? 0)} testId={`pro-clients-contributors-${ev.id}`} />
                <StatCell label="Pending" value={String(ev.pending_approvals_count ?? 0)} testId={`pro-clients-pending-${ev.id}`} />
                <StatCell label="Lookbook" value={lookbookStateLabel(ev.lookbook?.state)} testId={`pro-clients-lookbook-${ev.id}`} />
              </div>
              {/* Quick actions */}
              <div className="flex items-center gap-3 mt-3 pt-2.5 border-t border-[#E3E8EF]" onClick={(e) => e.stopPropagation()}>
                <span role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); openClone(ev); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); e.preventDefault(); openClone(ev); } }}
                  className="flex items-center gap-1 text-xs font-semibold text-[#146BFF] hover:underline cursor-pointer"
                  data-testid={`pro-clients-clone-${ev.id}`}
                ><Copy className="w-3 h-3" /> Duplicate</span>
                <span role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigate(`/host-guests?event_id=${ev.id}`); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); e.preventDefault(); navigate(`/host-guests?event_id=${ev.id}`); } }}
                  className="flex items-center gap-1 text-xs font-semibold text-[#146BFF] hover:underline cursor-pointer"
                  data-testid={`pro-clients-invite-${ev.id}`}
                ><UserPlus className="w-3 h-3" /> Invite</span>
                {ev.lookbook?.state && (
                  <span role="button" tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigate(`/host-lookbook-live?event_id=${ev.id}`); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); e.preventDefault(); navigate(`/host-lookbook-live?event_id=${ev.id}`); } }}
                    className="flex items-center gap-1 text-xs font-semibold text-[#146BFF] hover:underline cursor-pointer"
                    data-testid={`pro-clients-lookbook-link-${ev.id}`}
                  ><BookOpen className="w-3 h-3" /> Lookbook</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Clone modal */}
      {cloneTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1B46]/55 p-4" onClick={() => setCloneTarget(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md flex flex-col gap-2" onClick={(e) => e.stopPropagation()} data-testid="pro-clients-clone-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-extrabold text-[#0B1B46]">Duplicate event</h3>
              <button onClick={() => setCloneTarget(null)} className="p-1 hover:bg-[#F3F5F8] rounded" aria-label="Close"><X className="w-5 h-5 text-[#5B6472]" /></button>
            </div>
            <p className="text-sm text-[#5B6472]">Copies settings only &mdash; no members, media, invitations, lookbook, or payments.</p>
            <label className="text-[11px] font-extrabold text-[#0B1B46] uppercase tracking-wide mt-2" htmlFor="pc-clone-name">New event name</label>
            <input id="pc-clone-name" type="text" value={cloneName} onChange={(e) => setCloneName(e.target.value)}
              placeholder="e.g. Sarah & James Wedding (copy)"
              className="w-full border border-[#E3E8EF] rounded-lg px-3 py-2.5 text-sm text-[#0B1B46] placeholder:text-[#5B6472] focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
              data-testid="pro-clients-clone-name-input" />
            <label className="text-[11px] font-extrabold text-[#0B1B46] uppercase tracking-wide mt-2" htmlFor="pc-clone-start">Start date &amp; time</label>
            <input id="pc-clone-start" type="datetime-local" value={cloneStart} onChange={(e) => setCloneStart(e.target.value)}
              className="w-full border border-[#E3E8EF] rounded-lg px-3 py-2.5 text-sm text-[#0B1B46] focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
              data-testid="pro-clients-clone-start-input" />
            <label className="text-[11px] font-extrabold text-[#0B1B46] uppercase tracking-wide mt-2" htmlFor="pc-clone-end">End date &amp; time</label>
            <input id="pc-clone-end" type="datetime-local" value={cloneEnd} onChange={(e) => setCloneEnd(e.target.value)}
              className="w-full border border-[#E3E8EF] rounded-lg px-3 py-2.5 text-sm text-[#0B1B46] focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
              data-testid="pro-clients-clone-end-input" />
            {cloneError && <p className="text-xs font-semibold text-[#DC2626] mt-1" data-testid="pro-clients-clone-error">{cloneError}</p>}
            <div className="flex gap-2 mt-3">
              <button onClick={() => setCloneTarget(null)}
                className="flex-1 h-10 border border-[#E3E8EF] bg-white hover:bg-[#F3F5F8] text-[#0B1B46] font-semibold rounded-lg transition-colors"
                data-testid="pro-clients-clone-cancel-btn">Cancel</button>
              <button onClick={onClone} disabled={cloneBusy}
                className="flex-1 h-10 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-60 text-white font-semibold rounded-lg transition-colors"
                data-testid="pro-clients-clone-submit-btn">{cloneBusy ? 'Cloning\u2026' : 'Duplicate'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
